import React, { useState, useRef, useEffect } from 'react';

const SimpleDropdown = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Select an option...",
  required = false,
  isLoading = false,
  allowCustom = true,
  onAddCustom = null
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showOptions, setShowOptions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPhase, setAiPhase] = useState('idle');
  const [generationProgress, setGenerationProgress] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = options.filter(option => 
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  useEffect(() => {
    if (isLoading && !isGenerating) {
      setIsGenerating(true);
      setAiPhase('analyzing');
      setGenerationProgress(0);
      
      // Immediate progress start
      let currentProgress = 0;
      const totalDuration = 1800; // 1.8 seconds
      const updateInterval = 40; // Update every 40ms
      const totalUpdates = totalDuration / updateInterval;
      const baseIncrement = 100 / totalUpdates;
      
      const progressInterval = setInterval(() => {
        currentProgress += baseIncrement + (Math.random() * 1 - 0.5); // Add slight randomness
        
        // Ensure we don't go over 100
        currentProgress = Math.min(currentProgress, 100);
        
        setGenerationProgress(currentProgress);
        
        if (currentProgress >= 100) {
          setAiPhase('complete');
          clearInterval(progressInterval);
          
          // Hide after completion
          setTimeout(() => {
            setIsGenerating(false);
            setAiPhase('idle');
            setGenerationProgress(0);
          }, 600);
        }
      }, updateInterval);
      
      return () => {
        clearInterval(progressInterval);
      };
    }
  }, [isLoading, isGenerating]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setShowOptions(true);
  };

  const handleOptionClick = (option) => {
    setInputValue(option);
    onChange(option);
    setShowOptions(false);
    
    // Hide AI animation when user selects option
    if (isGenerating) {
      setIsGenerating(false);
      setAiPhase('idle');
      setGenerationProgress(0);
    }
  };

  const handleInputFocus = () => {
    setShowOptions(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        // Check if it's an exact match (case insensitive)
        const exactMatch = options.find(o => o.toLowerCase() === inputValue.toLowerCase());
        if (exactMatch) {
          onChange(exactMatch);
        } else if (allowCustom) {
          // Add custom value
          onChange(inputValue.trim());
          if (onAddCustom) {
            onAddCustom(inputValue.trim());
          }
        }
        setShowOptions(false);
      }
    } else if (e.key === 'Escape') {
      setShowOptions(false);
    }
  };

  const handleAddCustom = () => {
    if (inputValue.trim()) {
      onChange(inputValue.trim());
      if (onAddCustom) {
        onAddCustom(inputValue.trim());
      }
      setShowOptions(false);
    }
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        
        {/* AI Indicator with Animation */}
        {options.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full shadow-sm">
            <div className="relative">
              {isGenerating ? (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                  <div className="w-1 h-1 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                </div>
              ) : (
                <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-ping"></div>
              )}
            </div>
            <span className="text-xs text-purple-700 font-semibold">
              {isGenerating ? (
                aiPhase === 'analyzing' ? 'AI Curating...' :
                aiPhase === 'complete' ? 'AI Ready!' : 'AI Processing...'
              ) : 'AI-Enhanced'}
            </span>
          </div>
        )}
      </div>

      {/* Advanced AI Processing Animation */}
      {isGenerating && (
        <div className="relative mb-4 p-4 bg-gradient-to-br from-purple-50 via-indigo-50 to-pink-50 rounded-xl border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {/* Neural Network Animation */}
              <div className="relative w-6 h-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 animate-spin" style={{animationDuration: '1.8s'}}></div>
                <div className="absolute inset-1 rounded-full bg-white flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-purple-700">
                  {aiPhase === 'analyzing' && '🎓 Curating education options...'}
                  {aiPhase === 'complete' && '✨ Education options ready!'}
                </span>
                <span className="text-xs text-purple-600 mt-1">
                  {aiPhase === 'analyzing' && 'Analyzing industry-specific requirements'}
                  {aiPhase === 'complete' && 'Personalized education suggestions available'}
                </span>
              </div>
            </div>
            
            {/* Progress Indicator */}
            <div className="text-xs font-mono text-purple-600 bg-white px-2 py-1 rounded">
              {Math.round(generationProgress)}%
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-purple-100 rounded-full h-1.5 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 via-indigo-500 to-pink-500 rounded-full transition-all duration-200 ease-out"
              style={{ width: `${generationProgress}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          required={required}
        />

        {/* Dropdown Arrow */}
        <div className="absolute right-3 top-2 flex items-center">
          <svg 
            className={`w-4 h-4 text-gray-400 transition-transform ${showOptions ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Options Dropdown */}
        {showOptions && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-xl max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 && (
              <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
                <div className="flex items-center gap-2 text-xs text-purple-700">
                  <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse"></div>
                  <span className="font-medium">AI-curated education options</span>
                </div>
              </div>
            )}
            
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleOptionClick(option)}
                className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 focus:bg-gradient-to-r focus:from-blue-50 focus:to-indigo-50 focus:outline-none transition-all duration-200 border-b border-gray-50 last:border-b-0"
              >
                <span className="text-sm text-gray-900 font-medium">{option}</span>
              </button>
            ))}
            
            {filteredOptions.length === 0 && inputValue.trim() && allowCustom && (
              <button
                type="button"
                onClick={handleAddCustom}
                className="w-full text-left px-4 py-3 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 focus:bg-gradient-to-r focus:from-green-50 focus:to-emerald-50 focus:outline-none transition-all duration-200 border-b border-gray-50"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-900 font-medium">Add custom: "{inputValue}"</span>
                    <span className="text-xs text-gray-500">Press Enter or click to add</span>
                  </div>
                </div>
              </button>
            )}
            
            {filteredOptions.length === 0 && (!inputValue.trim() || !allowCustom) && (
              <div className="px-4 py-3 text-sm text-gray-500">
                No matching options found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Help Text */}
      <p className="text-xs text-gray-500">
        {allowCustom 
          ? 'Select from AI-curated options, search, or type to add custom requirement'
          : 'Select from AI-curated options or type to search'
        }
      </p>
    </div>
  );
};

export default SimpleDropdown;
